/**
 * Artifact & signal-quality QC types: impedance timeline, reduction,
 * technologist sign-off, and reduction-action audit trail.
 */

export const ARTIFACT_QC_SCHEMA_VERSION = "artifact-qc-v1.0.0";

export const ARTIFACT_QC_INTENDED_USE =
  "Artifact markers, impedance timelines, and selective artifact-reduction views are technologist and clinician decision-support aids for signal-quality review. They do not replace raw EEG review or diagnostic interpretation.";

export const ARTIFACT_QC_EXCLUDED_USE =
  "Excluded use: permanent overwrite of original EEG samples, unsupervised clinical decision-making from reduced views, and QC sign-off by non-qualified roles.";

export type ImpedanceTimelinePoint = {
  timeSeconds: number;
  channel: string;
  impedanceKohms: number;
  quality: "good" | "fair" | "poor" | "unknown";
};

export type ElectrodeQualitySample = {
  channel: string;
  impedanceKohms: number | null;
  snrDb: number | null;
  isBad: boolean;
  quality: "good" | "fair" | "poor" | "unknown";
};

export type ArtifactReductionType =
  | "muscle"
  | "eye"
  | "electrode_pop"
  | "flatline"
  | "high_impedance"
  | "movement"
  | "noise";

export type ArtifactReductionState = {
  enabled: boolean;
  /** When true, viewer must show unmodified samples. */
  showOriginalSignal: boolean;
  reducedTypes: ArtifactReductionType[];
  label: string;
};

export type ArtifactReductionAction = {
  actionId: string;
  atIso: string;
  actorId: string | null;
  action:
    | "enable_reduction"
    | "disable_reduction"
    | "show_original"
    | "show_reduced"
    | "set_types";
  reducedTypes: ArtifactReductionType[];
  showOriginalSignal: boolean;
  notes?: string | null;
  schemaVersion: string;
};

export type QcSignOffStatus = "pending" | "signed" | "rejected" | "expired";

export type QcSignOffRecord = {
  status: QcSignOffStatus;
  technologistId: string | null;
  signedAtIso: string | null;
  notes: string | null;
  schemaVersion: string;
  blocksPhysicianReporting: boolean;
};

export function createDefaultReductionState(): ArtifactReductionState {
  return {
    enabled: false,
    showOriginalSignal: true,
    reducedTypes: [],
    label: "ORIGINAL SIGNAL — no artifact reduction applied",
  };
}

export function reductionViewLabel(state: ArtifactReductionState): string {
  if (!state.enabled || state.showOriginalSignal) {
    return "ORIGINAL SIGNAL PRESERVED — reduction view off";
  }
  const types =
    state.reducedTypes.length > 0
      ? state.reducedTypes.join(", ")
      : "selected artifact classes";
  return `MODIFIED VIEW — artifact reduction active (${types}); original samples preserved`;
}

export function applyReductionAction(
  state: ArtifactReductionState,
  action: Omit<ArtifactReductionAction, "actionId" | "atIso" | "schemaVersion"> & {
    actionId?: string;
    atIso?: string;
  },
): { state: ArtifactReductionState; audit: ArtifactReductionAction } {
  const next: ArtifactReductionState = { ...state };
  switch (action.action) {
    case "enable_reduction":
      next.enabled = true;
      next.showOriginalSignal = false;
      next.reducedTypes = [...action.reducedTypes];
      break;
    case "disable_reduction":
      next.enabled = false;
      next.showOriginalSignal = true;
      next.reducedTypes = [];
      break;
    case "show_original":
      next.showOriginalSignal = true;
      break;
    case "show_reduced":
      next.enabled = true;
      next.showOriginalSignal = false;
      break;
    case "set_types":
      next.reducedTypes = [...action.reducedTypes];
      break;
  }
  next.label = reductionViewLabel(next);
  const audit: ArtifactReductionAction = {
    actionId: action.actionId || `ar-${Date.now()}`,
    atIso: action.atIso || new Date().toISOString(),
    actorId: action.actorId,
    action: action.action,
    reducedTypes: [...next.reducedTypes],
    showOriginalSignal: next.showOriginalSignal,
    notes: action.notes ?? null,
    schemaVersion: ARTIFACT_QC_SCHEMA_VERSION,
  };
  return { state: next, audit };
}

export function createPendingQcSignOff(): QcSignOffRecord {
  return {
    status: "pending",
    technologistId: null,
    signedAtIso: null,
    notes: null,
    schemaVersion: ARTIFACT_QC_SCHEMA_VERSION,
    blocksPhysicianReporting: true,
  };
}

export function signOffQc(input: {
  technologistId: string;
  notes?: string | null;
  signedAt?: Date;
}): QcSignOffRecord {
  return {
    status: "signed",
    technologistId: input.technologistId,
    signedAtIso: (input.signedAt || new Date()).toISOString(),
    notes: input.notes ?? null,
    schemaVersion: ARTIFACT_QC_SCHEMA_VERSION,
    blocksPhysicianReporting: false,
  };
}

export function rejectQcSignOff(input: {
  technologistId: string;
  notes?: string | null;
}): QcSignOffRecord {
  return {
    status: "rejected",
    technologistId: input.technologistId,
    signedAtIso: new Date().toISOString(),
    notes: input.notes ?? null,
    schemaVersion: ARTIFACT_QC_SCHEMA_VERSION,
    blocksPhysicianReporting: true,
  };
}

export function buildImpedanceTimeline(
  samples: Array<{
    timeSeconds: number;
    channel: string;
    impedanceKohms: number;
  }>,
): ImpedanceTimelinePoint[] {
  return samples
    .filter(
      (sample) =>
        Number.isFinite(sample.timeSeconds) &&
        Number.isFinite(sample.impedanceKohms) &&
        sample.channel,
    )
    .map((sample) => {
      const z = sample.impedanceKohms;
      const quality: ImpedanceTimelinePoint["quality"] =
        z <= 5 ? "good" : z <= 10 ? "fair" : "poor";
      return {
        timeSeconds: sample.timeSeconds,
        channel: sample.channel,
        impedanceKohms: z,
        quality,
      };
    })
    .sort((a, b) => a.timeSeconds - b.timeSeconds);
}

export function summarizeElectrodeQuality(
  channels: Array<{
    channel: string;
    impedanceKohms?: number | null;
    snrDb?: number | null;
    isBad?: boolean;
  }>,
  classify: (
    snrDb: number | null | undefined,
    impedanceKohms: number | null | undefined,
  ) => "good" | "fair" | "poor" | "unknown",
): ElectrodeQualitySample[] {
  return channels.map((item) => ({
    channel: item.channel,
    impedanceKohms: item.impedanceKohms ?? null,
    snrDb: item.snrDb ?? null,
    isBad: Boolean(item.isBad),
    quality: item.isBad
      ? "poor"
      : classify(item.snrDb, item.impedanceKohms),
  }));
}
